/* eslint-disable */

import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { logOutUser, generateToastError } from '../../../utils';
import { queryKeys } from '../queryKeys';

// Checkin and Checkout Logic stars from here

const httpStatuses = { unauthorized: 401, error: 500 };

export const useQueryGetMeetingCredentialsToJoin = (inviteId, userToken, navigate, toast, setInviteId) => {
  const queryKey = [queryKeys.getMeetingCredentials];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/meeting/join/${inviteId}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, queryFn, {
    enabled: false,
    // retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess: (e) => {
      // if (!e?.data?.data?.joinUrl) {
      //   toast.error('There is an Error while fetching meeting credentials, Please contact Administrator!');
      //   setInviteId('')
      // }
    },
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      // generateToastError(toast, e);

      setInviteId('');
    },
  });
};

export const useMutationCancelAMeeting = (cancelId, toast, userToken, navigate, queryClient) => {
  const queryKey = [queryKeys.cancelAMeeting];
  const queryFn = () =>
    axios.put(
      `${process.env.REACT_APP_SERVER_URL}/api/meeting/cancel/${cancelId}`,
      {},
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.getUpcomingMeetingsList] });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

const ROLES_NOT_NEEDING_UPCOMING_MEETINGS = ['ADMIN'];

export const useQueryGetUpcomingMeetingsList = (userToken, navigate, toast, userRoleOrTotalRoles) => {
  const queryKey = [queryKeys.getUpcomingMeetingsList];

  const roles = Array.isArray(userRoleOrTotalRoles)
    ? userRoleOrTotalRoles
    : userRoleOrTotalRoles ? [userRoleOrTotalRoles] : [];
  const isAdmin = roles.some((r) => ROLES_NOT_NEEDING_UPCOMING_MEETINGS.includes(String(r || '').toUpperCase()));
  const shouldFetch = !!userToken && !isAdmin;

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/meeting/upcoming-meetings`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, queryFn, {
    enabled: shouldFetch,
    retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

//Join and Cancel Meeting flow//

/**
 * Hook to save AI note-taker permission for a meeting
 * 
 * @param {string} userToken - User authentication token
 * @param {function} toast - Toast notification function (optional)
 * @param {function} navigate - Navigation function (optional)
 * @returns {object} Mutation result with mutate function
 */
export const useMutationSaveAIPermission = (userToken, toast, navigate) => {
  const queryKey = [queryKeys.saveAIPermission];

  const queryFn = (variables) => {
    const { meetingId, permission } = variables;
    return axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/meeting/ai-permission/${meetingId}`,
      { permission },
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );
  };

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      console.log('✅ AI permission saved successfully!', success.data);
    },
    onError: (error) => {
      console.error('❌ Error saving AI permission:', error);
      if (error?.response?.status === httpStatuses.unauthorized) {
        if (navigate) {
          logOutUser(navigate);
        }
        return;
      }
      if (toast) {
        generateToastError(toast, error);
      }
    },
  });
};

/**
 * Hook to save meeting transcript and generate AI summary
 * 
 * @param {string} userToken - User authentication token
 * @param {function} toast - Toast notification function (optional)
 * @param {function} navigate - Navigation function (optional)
 * @returns {object} Mutation result with mutate function
 */
/**
 * Hook to fetch all meetings for admin / site leader / principal
 *
 * @param {string} userToken - User authentication token
 * @param {object} filters   - { status, search, page, limit }
 * @param {function} navigate
 * @param {function} toast
 */
export const useQueryGetAllMeetingsList = (userToken, filters = {}, navigate, toast) => {
  const { status = 'all', search = '', page = 1, limit = 20, schoolId = '' } = filters;
  const queryKey = [queryKeys.getAllMeetingsList, status, search, page, limit, schoolId];

  const queryFn = () => {
    const params = new URLSearchParams({ status, page, limit });
    if (search) params.append('search', search);
    if (schoolId) params.append('schoolId', schoolId);
    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/meeting/all?${params.toString()}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  };

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 1,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      if (toast) generateToastError(toast, e);
    },
  });
};

export const useMutationSaveMeetingTranscript = (userToken, toast, navigate) => {
  const queryKey = [queryKeys.saveMeetingTranscript];

  const queryFn = (variables) => {
    const { meetingId, transcript } = variables;
    return axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/meeting/transcript/${meetingId}`,
      { transcript },
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );
  };

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success, variables) => {
      console.log('✅ Transcript sent successfully!', success.data);
      
      const responseData = success?.data?.data || success?.data;
      const meetingId = variables?.meetingId;
      
      // Check if both participants completed surveys
      // If summaryPending is false/undefined, it means both completed surveys
      const bothSurveysCompleted = !responseData?.summaryPending;
      
      // Only clear localStorage if:
      // 1. Both surveys completed AND
      // 2. Summary was NOT denied (summaryDenied is not true)
      // If AI permission was denied, keep the transcript as backup
      const shouldClearTranscript = bothSurveysCompleted && !responseData?.summaryDenied;
      
      if (shouldClearTranscript && meetingId) {
        // Both participants submitted surveys and AI summary was generated → clear localStorage
        try {
          const storageKey = `meeting_transcript_${meetingId}`;
          localStorage.removeItem(storageKey);
          console.log('🗑️ Transcript cleared from localStorage - both surveys completed and AI summary generated');
        } catch (error) {
          console.error('Error clearing transcript from localStorage:', error);
        }
      } else if (responseData?.summaryDenied && meetingId) {
        console.log('ℹ️ Keeping transcript in localStorage - AI permission was denied');
      }
      
      if (toast) {
        toast.success(success?.data?.message || 'Meeting summary will be emailed to both participants shortly!', {
          duration: 5000,
        });
      }
    },
    onError: (error) => {
      console.error('❌ Error sending transcript:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      if (error?.response?.status === httpStatuses.unauthorized) {
        if (navigate) {
          logOutUser(navigate);
        }
        return;
      }
      if (toast) {
        generateToastError(toast, error);
      }
    },
  });
};
